/// «Зарплата» and «Моя робота» in the panel (specs/payroll-periods.md) — its
/// own file, one line in uk.ts, so that sections built in parallel merge
/// cleanly.

const plural = (n: number, one: string, few: string, many: string): string => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

const sessions = (n: number) => `${n} ${plural(n, 'заняття', 'заняття', 'занять')}`
const times = (n: number) => `${n} ${plural(n, 'раз', 'рази', 'разів')}`
const people = (n: number) => `${n} ${plural(n, 'людина', 'людини', 'людей')}`

const kyivDayTime = (iso: string, timeZone = 'Europe/Kyiv') =>
  new Intl.DateTimeFormat('uk-UA', { timeZone, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  )

export const payrollUk = {
  nav: 'Зарплата',
  title: 'Зарплата',
  subtitles: {
    sheet: 'Місяць за місяцем: скільки нараховано кожному, що затверджено і що вже виплачено.',
    salaries: 'Оклади працівників. Новий оклад з місяця закриває попередній.',
  },
  tabs: { sheet: 'Відомість', salaries: 'Оклади' },
  prevMonth: 'Попередній місяць',
  nextMonth: 'Наступний місяць',
  unnamed: 'Без імені',

  status: {
    open: 'Відкритий',
    approved: 'Затверджено',
    approvedBy: (who: string, when: string) => `Затвердив(-ла) ${who}, ${when}`,
    reopened: (n: number) => `Відкривали знову ${times(n)}`,
    openNote: 'Суми рахуються наживо, доки місяць не затвердять.',
    approvedNote: 'Суми заморожено. Пізні відмітки й повернення видно окремо, у «Змінах після затвердження».',
  },

  tiles: {
    accrued: 'Нараховано',
    approved: 'Затверджено',
    paid: 'Виплачено',
    balance: 'Залишок',
    people: 'Людей',
    sessions: 'За заняття',
    salary: 'Оклад',
    adjustments: 'Коригування',
    sessionsShort: 'заняття',
    salaryShort: 'оклади',
    peopleNote: (n: number) => people(n),
  },

  columns: {
    person: 'Працівник',
    held: 'Проведено',
    sessions: 'За заняття',
    salary: 'Оклад',
    adjustments: 'Коригування',
    total: 'Разом',
    paid: 'Виплачено',
    balance: 'Залишок',
    state: 'Виплата',
  },
  heldNote: 'Проведено занять за розкладом — групові теж, навіть ті, за які не платять.',
  open: (name: string) => `Відкрити: ${name}`,
  empty: 'У цьому місяці нікому нічого не нараховано',
  emptyMessage: 'Тут зʼявляться люди з оплаченими заняттями, окладом, коригуваннями чи виплатами за місяць.',
  branchScopeNote: 'Ви бачите тих, хто вів заняття у ваших клубах. Людей лише з окладом бачить керівництво компанії.',

  payoutState: {
    unpaid: 'Не виплачено',
    advance: 'Аванс',
    partial: 'Частково',
    paid: 'Виплачено',
  },

  approve: {
    action: 'Затвердити',
    reopen: 'Відкрити знову',
    notOver: (from: string) => `Місяць ще не закінчився — затвердити можна з ${from}.`,
    unmarkedFirst: 'Спершу відмітьте заняття або погодьтеся затвердити без них.',
    confirmTitle: (month: string) => `Затвердити ${month}?`,
    confirmMessage:
      'Суми заморозяться: те, що зміниться пізніше, буде видно окремо. Після затвердження можна записувати основні виплати.',
    confirmUnmarked: (n: number) =>
      `${sessions(n)} без відмітки заплатять 0 — їх буде видно в рядку людини.`,
    done: 'Місяць затверджено',
    reopenTitle: (month: string) => `Відкрити ${month} знову?`,
    reopenMessage:
      'Заморожені суми буде знято, місяць знову рахуватиметься наживо. Записані виплати лишаться — це справжні гроші.',
    reason: 'Причина',
    reasonPlaceholder: 'Наприклад: забули штраф Марку',
    reasonRequired: 'Напишіть причину',
    reopened: 'Місяць відкрито знову',
  },

  unmarked: {
    title: (n: number) => `Не відмічено ${sessions(n)}`,
    message: 'Ніхто не відмітив, чи прийшли клієнти. Такі заняття не оплачуються, доки їх не відмітять.',
    open: 'Відмітити',
    panelTitle: 'Заняття без відмітки',
    panelDescription: 'Відмітьте кожне, і сума перерахується за мить. Тренер може відмітити своє заняття до кінця наступного дня.',
    accept: 'Затвердити без них — вони заплатять 0',
    noRights: 'Відмічати відвідування може адміністратор або тренер заняття.',
    empty: 'Усі заняття відмічено',
  },

  changes: {
    title: 'Зміни після затвердження',
    message: 'Після затвердження змінилися заняття. Гроші рахуються за затвердженими сумами, доки місяць не відкриють знову.',
    line: (name: string, before: string, after: string, delta: string) => `${name}: ${before} → ${after} (${delta})`,
  },

  audit: {
    title: 'Історія',
    approved: (who: string) => `Затвердив(-ла) ${who}`,
    reopened: (who: string) => `Відкрив(-ла) знову ${who}`,
    total: (amount: string) => `Разом ${amount}`,
    acceptedUnmarked: (n: number) => `без відмітки: ${sessions(n)}`,
    empty: 'Місяць ще не затверджували',
  },

  person: {
    lines: 'Підсумок',
    sessionsTitle: 'Заняття',
    sessionsEmpty: 'Цього місяця занять немає',
    sessionsNote: (confirmed: number, planned: number, noShow: number) =>
      [`оплачено ${confirmed}`, planned ? `без відмітки ${planned}` : '', noShow ? `не прийшли ${noShow}` : '']
        .filter(Boolean)
        .join(' · '),
    salaryTitle: 'Оклад',
    salaryNone: 'Без окладу',
    salarySince: (from: string) => `з ${from}`,
    salarySpan: (from: string, to: string) => `${from} – ${to}`,
    salaryManage: 'Оклади',
    adjustmentsTitle: 'Коригування',
    adjustmentsEmpty: 'Коригувань немає',
    payoutsTitle: 'Виплати',
    payoutsEmpty: 'Виплат ще не було',
    voided: (reason: string) => `Скасовано: ${reason}`,
    void: 'Скасувати',
    approvedLocked: 'Місяць затверджено — коригувати можна, лише відкривши його знову.',
    marks: 'Історія відміток',
    marksEmpty: 'Відміток ще не було',
    noMark: 'Ще не відмічено',
    loadMarks: 'Хто відмічав',
  },

  marks: {
    byTrainer: 'Відмітив тренер',
    byDesk: 'Відмітив адміністратор',
    correctedByTrainer: 'Виправив тренер',
    correctedByDesk: 'Виправив адміністратор',
    at: (text: string, when: string) => `${text} · ${when}`,
  },

  accrual: {
    planned: 'Без відмітки',
    confirmed: 'Оплачується',
    no_show: 'Не прийшов',
    cancelled: 'Скасовано',
    no_rate: 'Без ставки',
    no_payout: 'Не оплачується',
  } as Record<string, string>,

  attendance: {
    attended: 'Прийшов',
    noShow: 'Не прийшов',
    booked: 'Записаний',
    cancelled: 'Скасував запис',
    makeAttended: 'Позначити «Прийшов»',
    makeNoShow: 'Позначити «Не прийшов»',
  },

  adjustment: {
    add: 'Додати коригування',
    kind: 'Що це',
    kinds: { bonus: 'Бонус', fine: 'Штраф', other: 'Інше' } as Record<string, string>,
    amount: 'Сума, ₴',
    amountHintOther: 'Мінус — щоб відняти, наприклад −300',
    comment: 'Коментар',
    commentPlaceholder: 'За що — це побачить працівник',
    commentRequired: 'Напишіть коментар',
    amountRequired: 'Вкажіть суму',
    save: 'Додати',
    voidTitle: 'Скасувати коригування?',
    voidMessage: 'Воно перестане рахуватися, але лишиться в історії з причиною.',
  },

  payout: {
    add: 'Записати виплату',
    kind: 'Виплата',
    kinds: { advance: 'Аванс', main: 'Основна' } as Record<string, string>,
    mainNeedsApproval: 'Основну виплату можна записати після затвердження місяця.',
    method: 'Як',
    methods: { cash: 'Готівка', card: 'Картка', fop_transfer: 'Переказ ФОП' } as Record<string, string>,
    cashNote: 'Готівкова виплата поки не потрапляє в касову зміну — видайте гроші з каси окремо.',
    amount: 'Сума, ₴',
    amountRequired: 'Вкажіть суму',
    paidOn: 'Дата',
    comment: 'Коментар',
    commentPlaceholder: 'Необовʼязково',
    above: (balance: string) => `Це більше за залишок (${balance}) — залишок стане відʼємним. Записати можна.`,
    save: 'Записати',
    voidTitle: 'Скасувати виплату?',
    voidMessage: 'Вона перестане рахуватися, але лишиться в історії з причиною.',
    line: (method: string, date: string) => `${method} · ${date}`,
  },

  void: {
    reason: 'Причина',
    reasonRequired: 'Напишіть причину',
    confirm: 'Скасувати запис',
    keep: 'Лишити',
  },

  salaries: {
    add: 'Новий оклад',
    empty: 'Окладів ще немає',
    emptyMessage: 'Оклад — щомісячна сума, яку людина отримує незалежно від занять.',
    columns: { person: 'Працівник', amount: 'Оклад', span: 'Діє', state: 'Стан', comment: 'Коментар' },
    span: (from: string, to: string | null) => (to ? `${from} – ${to}` : `з ${from}`),
    state: { current: 'Діє', upcoming: 'З наступних місяців', ended: 'Завершився', retired: 'Скасовано' } as Record<string, string>,
    panelTitle: 'Новий оклад',
    panelDescription: 'Діє з обраного місяця до наступної зміни. Попередній оклад людини закінчиться місяцем раніше.',
    person: 'Працівник',
    personPlaceholder: 'Оберіть людину',
    personRequired: 'Оберіть людину',
    amount: 'Сума на місяць, ₴',
    amountRequired: 'Вкажіть суму',
    from: 'З місяця',
    comment: 'Коментар',
    commentPlaceholder: 'Необовʼязково',
    save: 'Зберегти оклад',
    retire: 'Скасувати оклад',
    retireTitle: 'Скасувати оклад?',
    retireMessage: (name: string, amount: string) =>
      `${name} більше не отримуватиме ${amount} на місяць. Затверджені місяці збережуть стару суму.`,
    readOnly: 'Змінювати оклади може той, хто веде зарплату всієї компанії.',
    noProration: 'Оклад нараховується за весь місяць, без перерахунку за днями.',
  },

  myWork: {
    nav: 'Моя робота',
    title: 'Моя робота',
    subtitle: 'Ваші заняття, відмітки і заробіток за місяць.',
    upcoming: 'Найближчі заняття',
    upcomingEmpty: 'Найближчого тижня занять немає',
    yesterday: 'Вчора',
    today: 'Сьогодні',
    tomorrow: 'Завтра',
    noBookings: 'Ніхто не записався',
    places: (booked: number, capacity: number) => `${booked} з ${capacity}`,
    notStarted: 'Відмітити можна, коли заняття почнеться',
    cancelledSession: 'Заняття скасовано',
    month: 'Місяць',
    stats: {
      held: 'Проведено',
      heldNote: (personal: number, group: number) => `персональних ${personal} · групових ${group}`,
      attendees: 'Відвідувачів',
      noShows: 'Не прийшли',
      unmarked: 'Без відмітки',
      unmarkedNote: 'Відмітьте — інакше не оплатиться',
    },
    pay: 'Мій заробіток',
    payNoteOpen: 'Місяць відкритий: суми можуть змінитися до затвердження.',
    payNoteApproved: (when: string) => `Затверджено ${when}`,
    forecast: 'Прогноз',
    forecastNote: (n: number) => `ще ${sessions(n)} попереду`,
    breakdown: 'Із чого складається',
    sessionsLine: 'За заняття',
    salaryLine: 'Оклад',
    payouts: 'Виплати',
    payoutsEmpty: 'Цього місяця виплат ще не було',
    changed: (delta: string) => `Після затвердження змінилося на ${delta}. Виплати рахуються за затвердженою сумою.`,
    analytics: 'Кожне заняття окремо — в «Аналітиці»',
    statsUnavailable: 'Розклад зараз не відповідає — статистику занять покажемо пізніше.',
  },

  errors: {
    periodNotOver: 'Місяць ще не закінчився — затвердити його можна з першого числа наступного.',
    unmarkedSessions: (n: number) =>
      `Не відмічено ${sessions(n)}. Відмітьте їх або погодьтеся затвердити без них.`,
    periodApproved: 'Місяць уже затверджено. Щоб щось змінити, відкрийте його знову.',
    periodOpen: 'Місяць уже відкритий.',
    mainNeedsApproval: 'Основну виплату можна записати лише після затвердження місяця. Зараз запишіть аванс.',
    salaryOverlap: 'На ці місяці в людини вже є інший оклад.',
    alreadyVoided: 'Цей запис уже скасовано.',
    outsideTrainerWindow: (until: string | null) =>
      until
        ? `Тренер відмічає заняття до кінця наступного дня (до ${kyivDayTime(until)}). Тепер це може зробити адміністратор.`
        : 'Тренер відмічає заняття до кінця наступного дня. Тепер це може зробити адміністратор.',
    companyScope: 'Зарплату змінює той, хто веде її для всієї компанії, а не окремих клубів.',
    gone: 'Цього запису вже немає — оновіть сторінку.',
  },
}
